import {ActionIcon, Button, Group} from "@mantine/core";
import {Paper} from "@mantine/core";
import {NumberInput} from "@mantine/core";
import {DatePickerInput} from "@mantine/dates";
import { Copy as IconCopy } from "@phosphor-icons/react";
import {useCallback, useContext, useMemo, useState} from "react";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import {Row, Col} from "../../../components/Flex/Flex";
import Label from "../../../components/Label/Label.tsx";
import Page from "../../../components/Page/Page.tsx";
import MessageContext from "../../../context/MessageContext.tsx";
import CGT from "../../../utils/CGT.ts";
import style from './DiscordTimestamp.module.css';


const DISCORD_DATETIME = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
}
const DISCORD_DATETIME_DAY = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
    hour: 'numeric',
    minute: 'numeric',
}

const examples = [
    {title: 'US', zones: ['America/Los_Angeles', 'America/Phoenix', 'America/Denver', 'America/Chicago', 'America/New_York']},
    {title: 'Europe', zones: ['Europe/Lisbon', 'Europe/Berlin', 'Europe/Athens']},
    {title: 'Australia', zones: ['Australia/Perth', 'Australia/Sydney']}
];

function DiscordTimestamp() {
    const [time, setTime] = useState(CGT.now().set({second: 0}));

    const cgt = useMemo(() => time.toCGTObject(), [time]);

    const updateCGT = (type, val) => {
        let next = {...cgt};
        next[type] = val;
        setTime(CGT.fromCGT(next.year, next.day, next.hour, next.minutes));
    }

    const rows = useMemo(() => {
        let timeInSeconds = time.toSeconds().toFixed(0);
        const shortDate = {
            key: 'short-date',
            example: time.toLocaleString(CGT.DATE_SHORT),
            syntax: `<t:${timeInSeconds}:d>`,
        };
        const longDate = {
            key: 'long-date',
            example: time.toLocaleString(CGT.DATE_FULL),
            syntax: `<t:${timeInSeconds}:D>`,
        };
        const shortTime = {
            key: 'short-time',
            example: time.toLocaleString(CGT.TIME_SIMPLE),
            syntax: `<t:${timeInSeconds}:t>`,
        };
        const longTime = {
            key: 'long-time',
            example: time.toLocaleString(CGT.TIME_WITH_SECONDS),
            syntax: `<t:${timeInSeconds}:T>`,
        };
        const shortFull = {
            key: 'short-full',
            example: time.toLocaleString(DISCORD_DATETIME),
            syntax: `<t:${timeInSeconds}:f>`,
        };
        const longFull = {
            key: 'long-full',
            example: time.toLocaleString(DISCORD_DATETIME_DAY),
            syntax: `<t:${timeInSeconds}:F>`,
        };
        const relativeTime = {
            key: 'relative',
            example: time.toRelative(),
            syntax: `<t:${timeInSeconds}:R>`,
        };
        const unixTs = {
            key: 'unix',
            example: timeInSeconds,
            syntax: timeInSeconds,
        };
        return [shortDate, longDate, shortTime, longTime, shortFull, longFull, relativeTime, unixTs];
    }, [time]);


    const onSetDay = useCallback(date => {
        setTime(prev => {
            let cgt = CGT.fromFormat(date, 'yyyy-MM-dd');
            return cgt.set({hour: prev.hour, minute: prev.minute});
        })
    }, []);
    const onSetHour = useCallback(val => setTime(prev => prev.set({hour: val})), []);
    const onSetMinute = useCallback(val => setTime(prev => prev.set({minute: val})), []);

    return (
        <Page>
            <title>Discord Timestamp</title>
            <Paper shadow="xs" p="md" withBorder>
                <h2>Discord Timestamp generator</h2>
                <p>
                    Tired of running into issues sharing exact times with other combiners all over the globe?
                </p>

                <Row align="end">
                    <Col>
                        <Label>Your local time ({time.toFormat('ZZZZ')})</Label>
                        <Row>
                            <Col><DatePickerInput value={time.toJSDate()} onChange={onSetDay}/></Col>
                            <Col><NumberInput value={time.hour} onChange={onSetHour} style={{width: 70}}/></Col>
                            <Col style={{alignSelf: 'center'}}>:</Col>
                            <Col><NumberInput value={time.minute} onChange={onSetMinute} style={{width: 70}}/></Col>
                        </Row>

                    </Col>
                    <Col grow={1} shrink={1}/>
                    <Col>
                        <Label>Combine time</Label>
                        <Row>
                            <Col><Group>Y <NumberInput value={cgt.year} onChange={val => updateCGT('year', val)} style={{width: 70}}/></Group></Col>
                            <Col><Group>D <NumberInput value={cgt.day} onChange={val => updateCGT('day', val)} style={{width: 70}}/></Group></Col>
                            <Col>
                                <Row>
                                    <Col><NumberInput value={cgt.hour} onChange={val => updateCGT('hour', val)} style={{width: 70}}/></Col>
                                    <Col style={{alignSelf: 'center'}}>:</Col>
                                    <Col><NumberInput value={cgt.minutes} onChange={val => updateCGT('minutes', val)} style={{width: 70}}/></Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div className={style.wrapper}>
                            <table className={style.table}>
                                <thead>
                                <tr>
                                    <th>Syntax</th>
                                    <th>Example</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rows.map(row => <StampRow key={row.key} row={row}/>)}
                                </tbody>
                            </table>
                        </div>
                    </Col>
                    <Col grow={1} shrink={1}/>
                    <Col>
                        <div className={style.wrapper}>
                            <table className={style.tableExample}>
                                <thead>
                                <tr>
                                    <th>General Area</th>
                                    <th>Timezone</th>
                                    <th>Times</th>
                                </tr>
                                </thead>
                                <tbody>
                                {examples.map(group => <ExampleGroup key={group.title} group={group} time={time}/>)}
                                </tbody>
                            </table>
                        </div>
                    </Col>
                </Row>


            </Paper>
        </Page>
    )
}

const StampRow = ({row}) => {
    const {success, error} = useContext(MessageContext);
    return (
        <tr className={style.row}>
            <td>
                <Row align="stretch" wrap={false}>
                    <ActionIcon><IconCopy size={16} onClick={() => {
                        navigator.clipboard.writeText(row.syntax).then(() => {
                            success('Content copied to the clipboard')
                        }).catch(err => {
                            error("Cannot copy to clipboard", err.message);
                        })
                    }} /></ActionIcon>
                    <code className={style.syntax}>{row.syntax}</code>
                </Row>
            </td>
            <td className={style.cell}>
                {row.example}
            </td>
        </tr>

    );
}

const ExampleGroup = ({time, group}) => {
    return (
        <>
            {group.zones.map((row, i, list) => <ExampleRow key={row} row={row} time={time} blockTitle={group.title} span={i === 0 ? list.length : 0}/>)}
        </>
    );
}

const ExampleRow = ({time, row, blockTitle, span}) => {
    let loc = time.setZone(row);
    return (
        <tr className={span > 0 ? style.first : ''}>
            {span > 0 ? <td rowSpan={span}>{blockTitle}</td> : null}
            <td>{loc.zoneName}</td>
            <td>{loc.toLocaleString(CGT.DATETIME_SHORT)}</td>
        </tr>
    )
}


export default withRouteErrorBoundary(DiscordTimestamp);
